import type React from 'react';
import {useState} from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {errorText} from '../../hooks/notices';
import type {ModelInput, ShouldCost} from '../../types/shouldCostTypes';

type Props = {
    templates: ShouldCost['options']['templates'];
    category: string;
    /** 'template' starts from a template; 'scratch' from a single line to edit afterwards. */
    start: 'template' | 'scratch';
    onSubmit: (input: ModelInput) => Promise<unknown>;
    onClose: () => void;
};

const thisMonth = () => new Date().toISOString().slice(0, 7);

/** New price model (spec 4.9 "Start from a template / scratch"): name, unit, base month, volume, and the starting lines. */
export const NewModelDialog: React.FC<Props> = ({templates, category, start, onSubmit, onClose}) => {
    const [name, setName] = useState(`${category} price model`);
    const [unit, setUnit] = useState('unit');
    const [currency, setCurrency] = useState('EUR');
    const [baseMonth, setBaseMonth] = useState(thisMonth());
    const [volume, setVolume] = useState('');
    const [template, setTemplate] = useState(templates[0]?.key ?? '');
    const [basePrice, setBasePrice] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const price = Number(basePrice);
    const ready = name.trim() && /^\d{4}-\d{2}$/.test(baseMonth) && price > 0;
    const picked = templates.find(t => t.key === template);

    const submit = () => {
        setBusy(true);
        setError(null);
        const common = {name: name.trim(), unit: unit.trim() || 'unit', currency: currency.trim().toUpperCase() || 'EUR',
            baseMonth: `${baseMonth}-01`, annualVolume: Number(volume) || 0};
        const input: ModelInput = start === 'template'
            ? {...common, template, basePrice: price}
            : {...common, lines: [{name: 'Cost', baseCost: price, seriesId: null, tier: 'estimated'}]};
        onSubmit(input).then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-model-title">
            <DialogTitle id="cp-model-title">{start === 'template' ? 'New price model from a template' : 'New price model'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField label="Model name" size="small" value={name} onChange={e => setName(e.target.value)}
                               slotProps={{htmlInput: {maxLength: 200}}}/>
                    {start === 'template' && (
                        <TextField select label="Template" size="small" value={template} onChange={e => setTemplate(e.target.value)}
                                   helperText={picked ? `Lines: ${picked.lines.join(', ')}` : undefined}>
                            {templates.map(t => <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>)}
                        </TextField>
                    )}
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Unit" size="small" value={unit} onChange={e => setUnit(e.target.value)} sx={{flex: 1}}
                                   helperText="What one unit is: m², hour, kg…" slotProps={{htmlInput: {maxLength: 40}}}/>
                        <TextField label="Currency" size="small" value={currency} onChange={e => setCurrency(e.target.value)} sx={{flex: 1}}
                                   slotProps={{htmlInput: {maxLength: 3}}}/>
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Base month" size="small" type="month" value={baseMonth} sx={{flex: 1}}
                                   onChange={e => setBaseMonth(e.target.value)} slotProps={{inputLabel: {shrink: true}}}
                                   helperText="Month the base costs are priced in"/>
                        <TextField label="Base price per unit" size="small" type="number" value={basePrice} sx={{flex: 1}}
                                   onChange={e => setBasePrice(e.target.value)}
                                   helperText={start === 'template' ? 'Split over the template lines' : 'One line to break down afterwards'}/>
                        <TextField label="Annual volume" size="small" type="number" value={volume} sx={{flex: 1}}
                                   onChange={e => setVolume(e.target.value)} helperText="Units a year"/>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Lines with a cost driver move with their market index from the base month. Edit lines, drivers and
                        tiers after creating the model.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !ready}>Create model</Button>
            </DialogActions>
        </Dialog>
    );
};
